/**
 * plan의 셀 알림을 IXD 셀에 붙일 문구로 바꾼다. React·DOM 무관 순수 로직.
 *
 * 단위 배율 알림은 값 반영으로 실제 값이 들어간 셀에만 붙인다 — 반영 전에는
 * 그 셀에 아직 아무 값도 들어가지 않았으므로 확인할 것이 없다.
 */

import * as Sentry from "@sentry/react";

import type { IxdCellNotice } from "@/components/ixd-preview";
import type { CellIdentityKey } from "@/utils/ixd-cell-model";
import type { PlanCellNotice } from "./plan-value-index";

/** DSD 표 단위 어휘(십~조)와 .frm 외화 단위(천·백만)가 줄 수 있는 배율 전부 */
const SCALE_LABEL: Record<number, string> = {
  1: "십",
  2: "백",
  3: "천",
  4: "만",
  5: "십만",
  6: "백만",
  7: "천만",
  8: "억",
  9: "십억",
  10: "백억",
  11: "천억",
  12: "조",
};

const scaleLabelOf = (scalePower: number): string => {
  const label = SCALE_LABEL[scalePower];
  if (label) return label;
  // 서버 계약 밖의 배율 — 이 알림만 숫자 표기로 두고 보고해 다음 값을 놓치지 않는다.
  Sentry.captureMessage("모르는 단위 배율", {
    level: "warning",
    extra: { scalePower },
  });
  return `10^${scalePower}`;
};

const toIxdCellNotice = (notice: PlanCellNotice): IxdCellNotice => {
  if (notice.kind === "not-applied") {
    return {
      tone: "blocked",
      description: "다른 항목과 자리가 겹쳐 반영하지 않았습니다.",
    };
  }
  const scale = scaleLabelOf(notice.scalePower);
  return {
    tone: "conflict",
    description: `통화 표기(${notice.currencyMarker})에 맞는 단위 배율(${scale})이 적용되었습니다. 문제 없는지 다시 한 번 확인해 주세요.`,
  };
};

export const buildPlanCellNotices = (
  cellNoticeByIdentityKey: ReadonlyMap<CellIdentityKey, PlanCellNotice>,
  appliedIdentityKeys: ReadonlySet<string>,
): Map<string, IxdCellNotice> => {
  const notices = new Map<string, IxdCellNotice>();
  for (const [identityKey, notice] of cellNoticeByIdentityKey) {
    if (
      notice.kind === "unit-assumed" &&
      !appliedIdentityKeys.has(identityKey)
    ) {
      continue;
    }
    notices.set(identityKey, toIxdCellNotice(notice));
  }
  return notices;
};

/**
 * 반영 직후 알림 패널을 열 셀 — 이번 반영으로 값이 들어간 셀 중 단위 배율 알림이
 * 있는 첫 셀. 없으면 undefined.
 */
export const pickUnitNoticeIdentityKey = (
  appliedIdentityKeys: readonly string[],
  cellNoticeByIdentityKey: ReadonlyMap<CellIdentityKey, PlanCellNotice>,
): string | undefined =>
  appliedIdentityKeys.find(
    (key) => cellNoticeByIdentityKey.get(key)?.kind === "unit-assumed",
  );
